import Promise from 'bluebird';
import { createSelectOptionsFromSysEnums } from '../../../../shared/form-elements/select';
import { SubmissionError, reset, destroy, getFormValues } from 'redux-form';

import find from 'lodash/find';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import qs from 'qs';

import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';
import { actions as rootActions } from '../../../../root';
import { actions as parentActions } from '../../../../affiliates/profile';
import { getTargetValues } from '../../../../programs/components/forms/targeting';

const defaultInitialState = {
	listOfActiveAffiliatesPanIndex: [],
	listOfActiveOffersPanIndex: {},
	selectedCurrenciesPanIndexMap: {},
	shouldShowAddAffiliateSelect: false,
	shownTargetPixelHeightsMap: {},
	field: {
		location_id: null,
		currency: null,
		amount: null,
		status: 'active'
	},
	searchFormInitialValue: '',
	statusesFormInitialValues: {
		new: false,
		inactive: false,
		active: false,
		archived: false
	},
	statusesFormCurrentValues: {},
	query: {
		limit: 20,
		offset: 0,
		search: '',
		statuses: []
	},
	nullifiedBoosts: []
};

const createChildAffiliatesForm = ({ namespace, stateNodeName }) => {
	// action types
	const CHILD_AFFILIATES_FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const CHILD_AFFILIATES_FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;

	const SHOW_AFFILIATE_OFFERS = `${namespace}/${stateNodeName}-show-offers`;
	const HIDE_AFFILIATE_OFFERS = `${namespace}/${stateNodeName}-hide-offers`;

	const SHOW_OFFER_TARGETS = `${namespace}/${stateNodeName}-show-targets`;
	const HIDE_OFFER_TARGETS = `${namespace}/${stateNodeName}-hide-targets`;

	const FETCH_SUGGEST_SUCCESS = `${namespace}/fetch-suggest-success`;
	const FETCH_SUGGEST_ERROR = `${namespace}/fetch-suggest-error`;

	const FETCH_AFFILIATE_OFFERS_SUCCESS = `${namespace}/fetch-affiliate-offers-success`;
	const FETCH_AFFILIATE_OFFERS_ERROR = `${namespace}/fetch-affiliate-offers-error`;

	const CHANGE_CURRENCY = `${namespace}/${stateNodeName}-change-currency`;

	const DELETE_CHILD_AFFILIATE_SUCCESS = `${namespace}/${stateNodeName}-delete-child-aff-success`;
	const DELETE_CHILD_AFFILIATE_ERROR = `${namespace}/${stateNodeName}-delete-child-aff-error`;

	const ADD_CHILD_AFFILIATE_SUCCESS = `${namespace}/${stateNodeName}-add-child-aff-success`;
	const ADD_CHILD_AFFILIATE_ERROR = `${namespace}/${stateNodeName}-add-child-aff-error`;

	const TOGGLE_ADD_AFFILIATE_SELECT = `${namespace}/${stateNodeName}-toggle-add-affiliate-select`;
	const UPDATE_SHOWN_TARGETS_AMOUNT = `${namespace}/${stateNodeName}-update-shown-targets-amount`;

	const APPLY_AFFILIATES_TAB = `${namespace}/${stateNodeName}-apply-affiliates-tab`;
	const CLEAR_AFFILIATES_TAB = `${namespace}/${stateNodeName}-clear-affiliates-tab`;

	const SHOW_MORE = `${namespace}/${stateNodeName}-show-more`;
	const NULLIFY_BOOST = `${namespace}/${stateNodeName}-nullify-boost`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		submitAffiliatesForm: formData => (dispatch, getState) => {
			const state = getState();
			const { childAffiliatesForm, affiliateId } = state.affiliateProfile;
			const currencies = state.root.lists['available-currencies'];

			const dirtyData = formData.child_affiliates.map(aff => {
				return { affiliate_id: aff.affiliate[0].value, data: aff.offers };
			});

			if (dirtyData.length === 0) return;

			const affiliatesToSubmit = flatten(
				dirtyData.map(affiliate => {
					const res = affiliate.data.map(entity => {
						const fields = entity.targets.map(field => {
							const correctFields = currencies.filter(currency => !!field[currency.id]).map(currency => ({
								...childAffiliatesForm.field,
								...field,
								status: 'active',
								location_id: field['location_id'] && field['location_id'][0] ? field['location_id'][0]['value'] : '',
								currency: currency.id,
								amount: field[currency.id]
							}));

							return correctFields.length
								? correctFields
								: {
									...childAffiliatesForm.field
								};
						});

						return {
							affiliateId: affiliate.affiliate_id,
							offer: (entity.offer[0] && entity.offer[0].value) || null,
							fields: flatten(fields).filter(entity => !!entity.currency)
						};
					});

					return res.filter(entity => !isEmpty(entity.fields));
				})
			);

			const urls = affiliatesToSubmit.map(
				entity =>
					`${endpoints.OFFERS.INDIVIDUAL_TERMS.replace('{id}', entity.offer)}/${
						entity.affiliateId
					}?master_id=${affiliateId}`
			);
			const data = affiliatesToSubmit.map(entity => entity.fields);

			if (!urls || !urls.length || !data || !data.length) return;

			const request = http({
				url: urls,
				method: 'put',
				actions: [CHILD_AFFILIATES_FORM_SUBMIT_SUCCESS, CHILD_AFFILIATES_FORM_SUBMIT_ERROR],
				data
			});

			dispatch(parentActions.toggleProgressBar());

			return request(dispatch).then(action => {
				if (action.type === CHILD_AFFILIATES_FORM_SUBMIT_SUCCESS) {
					return dispatch(parentActions.fetchChildAffiliates(affiliateId, { shouldShowProgressBar: true }))
						.then(action => {
							return Promise.map(
								affiliatesToSubmit,
								aff => dispatch(actions.fetchAffiliateOffers(null, { value: aff.affiliateId })),
								{ concurrency: 2 }
							);
						})
						.then(() => dispatch(parentActions.toggleProgressBar()));
				}

				if (action.type === CHILD_AFFILIATES_FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
					dispatch(parentActions.toggleProgressBar());
					const validationsErrors = prepareValidationError(formData);
					throw new SubmissionError({
						child_affiliates: validationsErrors
					});
				}
			});
		},
		showAffiliateOffers: index => ({
			type: SHOW_AFFILIATE_OFFERS,
			payload: index
		}),

		hideAffiliateOffers: index => ({
			type: HIDE_AFFILIATE_OFFERS,
			payload: index
		}),

		showOfferTargets: (index, parentIndex) => ({
			type: SHOW_OFFER_TARGETS,
			payload: { index, parentIndex }
		}),

		hideOfferTargets: (index, parentIndex) => ({
			type: HIDE_OFFER_TARGETS,
			payload: { index, parentIndex }
		}),

		affiliatesLookups: text => (dispatch, getState) => {
			const { childAffiliates, affiliateId } = getState().affiliateProfile;
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					const filtered = action.payload.data.filter(entity => {
						return entity.id !== parseInt(affiliateId) && !find(childAffiliates, item => entity.id === item.id);
					});
					return {
						options: createSelectOptionsFromSysEnums(filtered)
					};
				}
			});
		},

		geosConversionsLookups: () => (dispatch, getState) => {
			const { affiliateId } = getState().affiliateProfile;
			const request = http({
				url: endpoints.LOOKUPS.CONVERSIONS_GEOS.replace('{id}', affiliateId),
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: action.payload.data
					};
				}
			});
		},

		affiliatesOffersLookups: (childAffiliatesIndex, text) => (dispatch, getState) => {
			const { childAffiliates } = getState().affiliateProfile;
			const affiliate = childAffiliates[childAffiliatesIndex];

			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS}?${qs.stringify({ query: text })}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					const filtered = action.payload.data.filter(entity => {
						return !find(affiliate.offer_ids, savedOffers => savedOffers.id === entity.id);
					});
					return {
						options: createSelectOptionsFromSysEnums(filtered)
					};
				}
			});
		},

		fetchAffiliateOffers: (index, affiliate) => (dispatch, getState) => {
			if (!affiliate || !affiliate.value) return null;

			const state = getState();
			const { childAffiliates, offers } = state.affiliateProfile;
			const childAffiliateToFetch = find(childAffiliates, { id: affiliate.value });

			if (
				!childAffiliateToFetch ||
				!Array.isArray(childAffiliateToFetch.offer_ids) ||
				!childAffiliateToFetch.offer_ids.filter(Boolean).length
			) {
				// dont make unnecessary requests if affiliate has no offers
				return null;
			}

			!isNull(index) && dispatch(rootActions.spinnerToggle());

			const request = http({
				url: `${endpoints.OFFERS.BASE}?affiliate_id=${affiliate.value}`,
				method: 'get',
				actions: [FETCH_AFFILIATE_OFFERS_SUCCESS, FETCH_AFFILIATE_OFFERS_ERROR],
				addToResponse: {
					index,
					affiliate,
					masterOffers: offers
				}
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_AFFILIATE_OFFERS_SUCCESS) {
					if (!isNull(index)) {
						dispatch(rootActions.spinnerToggle());
						dispatch(actions.showAffiliateOffers(index));
					}
				}
			});
		},

		changeCurrency: (parentIndex, index, currency) => ({
			type: CHANGE_CURRENCY,
			payload: {
				parentIndex,
				index,
				currency
			}
		}),

		deleteAffiliate: id => (dispatch, getState) => {
			const { affiliateId } = getState().affiliateProfile;
			const request = http({
				url: `${endpoints.AFFILIATES.MASTER_ACCOUNT}/${affiliateId}`,
				method: 'delete',
				data: { id },
				actions: [DELETE_CHILD_AFFILIATE_SUCCESS, DELETE_CHILD_AFFILIATE_ERROR]
			});

			dispatch(destroy('childAffiliatesForm'));
			dispatch(parentActions.resetChildAffiliatesForm());

			request(dispatch).then(action => {
				if (action.type === DELETE_CHILD_AFFILIATE_SUCCESS) {
					dispatch(parentActions.toggleProgressBar());
					dispatch(parentActions.fetchChildAffiliates(affiliateId));
				}
			});
		},

		toggleAddAffiliateSelect: () => ({
			type: TOGGLE_ADD_AFFILIATE_SELECT
		}),

		addAffiliate: affiliate => (dispatch, getState) => {
			const id = affiliate && affiliate.value;
			const email = affiliate && affiliate.label;
			if (!id) {
				return;
			}

			const confirmation = prompt(
				`You are going to add ${email} to this master account.\n\n` +
					`All offers and individual terms will be removed from ${email}.\n\n` +
					`Are you sure you want to proceed? Type 'yes' to confirm.`
			);
			if (!confirmation || confirmation.toLowerCase() !== 'yes') {
				return dispatch(parentActions.resetChildAffiliatesForm());
			}

			const { affiliateId } = getState().affiliateProfile;
			const request = http({
				url: `${endpoints.AFFILIATES.MASTER_ACCOUNT}/${affiliateId}`,
				method: 'put',
				data: { id },
				actions: [ADD_CHILD_AFFILIATE_SUCCESS, ADD_CHILD_AFFILIATE_ERROR]
			});

			request(dispatch).then(action => {
				dispatch(destroy('childAffiliatesForm'));
				dispatch(parentActions.resetChildAffiliatesForm());

				dispatch(parentActions.toggleProgressBar());

				if (action.type === ADD_CHILD_AFFILIATE_SUCCESS) {
					dispatch(parentActions.fetchChildAffiliates(affiliateId));
				}
				if (action.type === ADD_CHILD_AFFILIATE_ERROR) {
					alert(action.payload.response.data.map(d => d.message));
					dispatch(parentActions.fetchChildAffiliates(affiliateId));
				}
			});
		},

		updateShownTargetsPixelHeights: (index, amount) => ({
			type: UPDATE_SHOWN_TARGETS_AMOUNT,
			payload: { index, amount }
		}),

		applyAffiliatesTab: () => (dispatch, getState) => {
			const { affiliateId } = getState().affiliateProfile;
			const affiliateInternalStatuses = getState().root.lists['affiliate-internal-statuses'];
			const searchFormValues = getFormValues('affiliateProfileAffiliatesTabSearchForm')(getState());
			const statusesFormValues = getFormValues('affiliateProfileAffiliateTabStatusesForm')(getState());
			const search = searchFormValues ? searchFormValues.search : null;
			const statuses = affiliateInternalStatuses.filter(s => !!statusesFormValues[s.id]).map(s => s.id);

			dispatch(destroy('childAffiliatesForm'));
			dispatch({
				type: APPLY_AFFILIATES_TAB,
				payload: {
					search,
					statuses
				}
			});

			dispatch(parentActions.toggleProgressBar());
			dispatch(
				parentActions.fetchChildAffiliates(affiliateId, {}, {
					search,
					statuses
				})
			);
		},

		clearAffiliatesTab: () => (dispatch, getState) => {
			const { affiliateId } = getState().affiliateProfile;
			dispatch(reset('affiliateProfileAffiliatesTabSearchForm'));
			dispatch(reset('affiliateProfileAffiliateTabStatusesForm'));
			dispatch(destroy('childAffiliatesForm'));
			dispatch({
				type: CLEAR_AFFILIATES_TAB
			});
			dispatch(parentActions.toggleProgressBar());
			dispatch(parentActions.fetchChildAffiliates(affiliateId));
		},

		showMore: () => (dispatch, getState) => {
			const { childAffiliatesForm: { query }, affiliateId } = getState().affiliateProfile;
			const newQuery = { ...query, offset: query.offset + query.limit };
			const fetchOptions = {
				shouldShowProgressBar: false,
				shouldAppendData: true
			};

			dispatch({
				type: APPLY_AFFILIATES_TAB,
				payload: newQuery
			});

			dispatch(parentActions.toggleProgressBar());
			dispatch(parentActions.fetchChildAffiliates(affiliateId, fetchOptions, newQuery));
		},

		nullifyBoostOnSubmit: target => ({
			type: NULLIFY_BOOST,
			payload: target
		})
	};

	// reducers
	const reducers = {
		[SHOW_AFFILIATE_OFFERS]: (state, action) => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				listOfActiveAffiliatesPanIndex: [].concat(
					state.childAffiliatesForm.listOfActiveAffiliatesPanIndex,
					action.payload
				)
			}
		}),

		[HIDE_AFFILIATE_OFFERS]: (state, action) => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				listOfActiveAffiliatesPanIndex: state.childAffiliatesForm.listOfActiveAffiliatesPanIndex.filter(
					index => index !== action.payload
				)
			}
		}),

		[SHOW_OFFER_TARGETS]: (state, action) => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				listOfActiveOffersPanIndex: {
					...state.childAffiliatesForm.listOfActiveOffersPanIndex,
					[action.payload.parentIndex]: [].concat(
						state.childAffiliatesForm.listOfActiveOffersPanIndex[action.payload.parentIndex] || [],
						action.payload.index
					)
				}
			}
		}),

		[HIDE_OFFER_TARGETS]: (state, action) => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				listOfActiveOffersPanIndex: {
					...state.childAffiliatesForm.listOfActiveOffersPanIndex,
					[action.payload.parentIndex]: state.childAffiliatesForm.listOfActiveOffersPanIndex[
						action.payload.parentIndex
					].filter(index => index !== action.payload.index)
				}
			}
		}),

		[UPDATE_SHOWN_TARGETS_AMOUNT]: (state, action) => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				shownTargetPixelHeightsMap: {
					...state.childAffiliatesForm.shownTargetPixelHeightsMap,
					[action.payload.index]:
						(state.childAffiliatesForm.shownTargetPixelHeightsMap[action.payload.index] || 0) + action.payload.amount
				}
			}
		}),

		[FETCH_AFFILIATE_OFFERS_SUCCESS]: (state, action) => ({
			...state,
			childAffiliates: state.childAffiliates.map(affiliate => {
				if (affiliate.id === action.meta.affiliate.value) {
					const offers = action.payload.data;
					const userOffers = offers
						.filter(o => affiliate.offer_ids.indexOf(o.id) !== -1)
						.filter(o => !!find(action.meta.masterOffers, { id: o.id }));
					const userOfferTerms = userOffers
						.map(offer => {
							return (
								offer.payouts &&
								offer.payouts.map(payout => ({
									...payout,
									affiliate_id: affiliate.id,
									offer: offer.title,
									offer_id: offer.id,
									status: payout.status
								}))
							);
						})
						.filter(Boolean);

					return {
						...affiliate,
						offers: userOffers,
						individualTerms: flatten(userOfferTerms)
					};
				}
				return affiliate;
			})
		}),

		[CHANGE_CURRENCY]: (state, action) => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				selectedCurrenciesPanIndexMap: {
					...state.childAffiliatesForm.selectedCurrenciesPanIndexMap,
					[action.payload.parentIndex]: {
						...(state.childAffiliatesForm.selectedCurrenciesPanIndexMap[action.payload.parentIndex] || {}),
						[action.payload.index]: action.payload.currency
					}
				}
			}
		}),

		[DELETE_CHILD_AFFILIATE_ERROR]: (state, action) => ({
			...state,
			error: true
		}),

		[TOGGLE_ADD_AFFILIATE_SELECT]: state => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				shouldShowAddAffiliateSelect: !state.childAffiliatesForm.shouldShowAddAffiliateSelect
			}
		}),

		[APPLY_AFFILIATES_TAB]: (state, action) => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				query: action.payload
			}
		}),

		[CLEAR_AFFILIATES_TAB]: (state, action) => ({
			...state,
			childAffiliatesForm: initialState
		}),

		[SHOW_MORE]: (state, action) => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				query: { ...state.childAffiliatesForm.query, ...action.payload }
			}
		}),

		[NULLIFY_BOOST]: (state, action) => ({
			...state,
			childAffiliatesForm: {
				...state.childAffiliatesForm,
				// concat if not already in state
				nullifiedBoosts: find(state.childAffiliatesForm.nullifiedBoosts, {
					offerId: action.payload.offerId,
					affiliateId: action.payload.affiliateId,
					locationId: action.payload.locationId
				})
					? state.childAffiliatesForm.nullifiedBoosts
					: state.childAffiliatesForm.nullifiedBoosts.concat(action.payload)
			}
		})
	};

	// selectors
	const selectors = {
		getFormData: state => {
			const {
				childAffiliates,
				childAffiliatesForm: {
					listOfActiveAffiliatesPanIndex,
					listOfActiveOffersPanIndex,
					selectedCurrenciesPanIndexMap,
					shouldShowAddAffiliateSelect,
					shownTargetPixelHeightsMap,
					searchFormInitialValue,
					statusesFormInitialValues,
					nullifiedBoosts
				}
			} = state.affiliateProfile;

			const masterOffers = state.affiliateProfile.offers;

			const mappedAffiliates = childAffiliates
				? childAffiliates.map(a => {
					let offers = a.offers || [];
					offers = offers.filter(o => !!find(masterOffers, { id: o.id }));
					const offerMasterTargets = masterOffers
						? flatten(masterOffers.map(o => {
							if (!o.payouts) return [];
							return o.payouts.map(p => ({ ...p, offer_id: o.id }));
						}))
						: [];
					const indTerms = a.individualTerms || [];
					const preparedOffers = getPreparedSavedOffers(offers, indTerms, offerMasterTargets);

					return {
						affiliate: [{ value: a.id, label: a.email }],
						offer_ids: a.offer_ids.filter(Boolean).filter(id => !!find(masterOffers, { id })),
						offers: preparedOffers
					};
				})
				: [];

			const initialValues = { child_affiliates: mappedAffiliates };

			return {
				initialValues,
				listOfActiveAffiliatesPanIndex,
				listOfActiveOffersPanIndex,
				selectedCurrenciesPanIndexMap,
				shouldShowAddAffiliateSelect,
				shownTargetPixelHeightsMap,
				searchFormInitialValue: { search: searchFormInitialValue },
				statusesFormInitialValues,
				nullifiedBoosts
			};
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions

const getPreparedSavedOffers = (offers, individualTerms, offerMasterTargets) =>
	offers.map(offer => {
		const groupTermsByOffer = groupBy(individualTerms, term => term.offer_id);
		const groupMasterTermsByOffer = groupBy(offerMasterTargets, term => term.offer_id);
		const targets = getTargetValues(groupTermsByOffer[offer.id]) || [];
		const masterTargets = getTargetValues(groupMasterTermsByOffer[offer.id]) || [];

		return {
			hasIndividualTerms: offer.payouts && offer.payouts[0] ? !!offer.payouts[0]['is_individual_term'] : false,
			offer: [
				{
					value: offer.id,
					label: offer.title,
					image: offer.image
				}
			],
			targets: targets
				.map(target => {
					let masterTarget = find(masterTargets, {
						location: target.location,
						offer_id: target.offer_id
					});

					return masterTarget && masterTarget.status === 'active' && { ...target, masterTarget };
				})
				.filter(Boolean)
		};
	});

// const getDirtyData = (savedValues, formValues, nullifiedBoosts) => {
// 	const res = [];
//
// 	formValues.forEach(formValueEntity => {
// 		if (isEmpty(savedValues)) {
// 			res.push(formValueEntity);
// 		} else if (isNonInInitialValues(formValueEntity, savedValues)) {
// 			res.push(formValueEntity);
// 		} else {
// 			savedValues.forEach(initialValueEntity => {
// 				if (formValueEntity.offer[0] && formValueEntity.offer[0].value === initialValueEntity.offer[0].value) {
// 					const changedTargets = formValueEntity.targets
// 						.map(newTarget => {
// 							const oldTarget = find(initialValueEntity.targets, {
// 								location: newTarget.location,
// 								offer_id: newTarget.offer_id,
// 								affiliate_id: newTarget.affiliate_id
// 							});
// 							return newTarget['rub'] != oldTarget['rub'] || newTarget['usd'] != oldTarget['usd'];
// 						})
// 						.filter(Boolean);
//
// 					const changedToNullTargetsResetToDefault = formValueEntity.targets.map(newTarget => {
// 						let initialTarget = find(initialValueEntity.targets, {
// 							location: newTarget.location,
// 							offer_id: newTarget.offer_id,
// 							affiliate_id: newTarget.affiliate_id
// 						});
// 						if (initialTarget) {
// 							let result = { ...initialTarget };
//
// 							let isChangedToNullRub = !isUndefined(initialTarget['rub']) && isEmpty(newTarget['rub']);
// 							let isChangedToNullUsd = !isUndefined(initialTarget['usd']) && isEmpty(newTarget['usd']);
//
// 							if (isChangedToNullRub || isChangedToNullUsd) {
// 								result.usd = isChangedToNullUsd ? initialTarget.usd : newTarget.usd;
// 								result.rub = isChangedToNullRub ? initialTarget.rub : newTarget.rub;
// 								return result;
// 							}
// 						}
// 						return newTarget;
// 					});
//
// 					const withBoostsReset = changedToNullTargetsResetToDefault.map(newTarget => {
// 						const oldTarget = find(initialValueEntity.targets, {
// 							location: newTarget.location,
// 							offer_id: newTarget.offer_id,
// 							affiliate_id: newTarget.affiliate_id
// 						});
//
// 						const wasBoostNullified = !!find(nullifiedBoosts, {
// 							offerId: newTarget.offer_id,
// 							locationId: newTarget.location_id[0].value,
// 							affiliateId: newTarget.affiliate_id
// 						});
//
// 						if (wasBoostNullified && (newTarget['rub'] != oldTarget['rub'] || newTarget['usd'] != oldTarget['usd'])) {
// 							return { ...newTarget, boost_till: null };
// 						}
// 						return newTarget;
// 					});
//
// 					// logic here, as in all ind terms forms, is that we submit all payouts if any of them changed
// 					// so that affiliate always has either base payouts or individual payouts for all available geos
// 					if (changedTargets.length) {
// 						res.push({
// 							offer: formValueEntity.offer,
// 							targets: withBoostsReset
// 						});
// 					}
// 				}
// 			});
// 		}
// 	});
//
// 	return res;
// };
// const isNonInInitialValues = (formValueEntity, initialValues) => {
// 	return isEmpty(
// 		initialValues.filter(
// 			initialValueEntity =>
// 				formValueEntity.offer[0] && formValueEntity.offer[0].value === initialValueEntity.offer[0].value
// 		)
// 	);
// };
const prepareValidationError = formData => {
	const validationsErrors = [];

	formData['child_affiliates'].forEach((affiliate, index) => {
		validationsErrors[index] = {
			offers: {}
		};

		affiliate['offers'].forEach((field, offersIndex) => {
			const validationsTargetsError = [];

			field['targets'].forEach((targetsField, targetsIndex) => {
				validationsTargetsError[targetsIndex] = {};
				if ((!targetsField.rub && !targetsField.usd) || (targetsField.rub == 0 || targetsField.usd == 0)) {
					validationsTargetsError[targetsIndex]['rub'] = 'rub or usd should be >= 0.01';
					validationsTargetsError[targetsIndex]['usd'] = 'usd or rub should be >= 0.01';
				} else {
					if (targetsField.rub && isNaN(parseFloat(targetsField.rub))) {
						validationsTargetsError[targetsIndex]['rub'] = 'rub must be number';
					}
					if (targetsField.usd && isNaN(parseFloat(targetsField.usd))) {
						validationsTargetsError[targetsIndex]['usd'] = 'usd must be number';
					}
				}
			});

			validationsErrors[index]['offers'][offersIndex] = {};
			validationsErrors[index]['offers'][offersIndex]['targets'] = validationsTargetsError;
		});
	});

	return validationsErrors;
};

export default createChildAffiliatesForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/components/profile/affiliates/index.js